import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatMoney, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type {
  Article,
  Partner,
  PurchaseLine,
  PurchaseOrder,
  PurchaseStatus,
  PurchaseSuggestion,
  Warehouse,
} from '../types';

const STATUS_STYLE: Record<PurchaseStatus, 'warn' | 'info' | 'ok' | 'default'> = {
  entwurf: 'default',
  bestellt: 'warn',
  teilgeliefert: 'info',
  geliefert: 'ok',
  storniert: 'default',
};

interface DraftLine {
  article: Article;
  qtyOrdered: string;
  price: string;
}

const fromCents = (cents: number | null) =>
  cents === null ? '' : (cents / 100).toFixed(2).replace('.', ',');

function toCents(value: string): number | null {
  const clean = value.replace(',', '.').trim();
  if (!clean) return null;
  const parsed = Number(clean);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
}

export function PurchasingPage() {
  const { owners, can } = useAuth();
  const mayEdit = can('admin', 'kaufmann', 'disponent');

  const [orders, setOrders] = useState<PurchaseOrder[] | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ order: PurchaseOrder; lines: PurchaseLine[] } | null>(null);
  const [proposals, setProposals] = useState<PurchaseSuggestion[]>([]);
  const [showProposals, setShowProposals] = useState(false);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<Partner[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({
    ownerId: '',
    supplierId: '',
    warehouseId: '',
    reference: '',
    expectedAt: '',
  });
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<Article[]>([]);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      setOrders(
        await api<PurchaseOrder[]>('/api/purchasing/orders', {
          query: { openOnly: openOnly ? 'true' : undefined },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }, [openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
    api<Partner[]>('/api/partners', { query: { role: 'supplier' } })
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
    api<PurchaseSuggestion[]>('/api/purchasing/suggestions')
      .then(setProposals)
      .catch(() => setProposals([]));
  }, []);

  useEffect(() => {
    if (!term.trim() || !draft.ownerId) {
      setHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<Article[]>('/api/articles/lookup', { query: { q: term.trim() } })
        .then((found) => setHits(found.filter((a) => a.owner_id === draft.ownerId)))
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [term, draft.ownerId]);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(null);
    try {
      setDetail(await api<{ order: PurchaseOrder; lines: PurchaseLine[] }>(`/api/purchasing/orders/${id}`));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (openId) void loadDetail(openId);
  }, [openId, loadDetail]);

  function addFromSuggestion(suggestion: PurchaseSuggestion) {
    setShowForm(true);
    setDraft((current) => ({ ...current, ownerId: suggestion.owner_id }));
    setDraftLines((current) => [
      ...current,
      {
        article: {
          id: suggestion.article_id,
          sku: suggestion.sku,
          name: suggestion.article_name,
          unit: suggestion.unit,
          owner_id: suggestion.owner_id,
        } as Article,
        qtyOrdered: String(suggestion.shortfall),
        price: fromCents(suggestion.price_cents),
      },
    ]);
  }

  async function createOrder() {
    if (draftLines.length === 0) return setError('Mindestens eine Position wird gebraucht.');
    setBusy(true);
    setError('');
    try {
      const created = await api<PurchaseOrder>('/api/purchasing/orders', {
        method: 'POST',
        body: {
          ownerId: draft.ownerId,
          supplierId: draft.supplierId,
          warehouseId: draft.warehouseId,
          reference: draft.reference || null,
          expectedAt: draft.expectedAt || null,
          lines: draftLines.map((line) => ({
            articleId: line.article.id,
            qtyOrdered: Number(line.qtyOrdered.replace(',', '.')),
            priceCents: toCents(line.price),
          })),
        },
      });
      setMessage(`Bestellung ${created.number} angelegt.`);
      setShowForm(false);
      setDraftLines([]);
      await load();
      setOpenId(created.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function act(path: string, success: string) {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      const result = await api<{ number?: string }>(`/api/purchasing/orders/${detail.order.id}/${path}`, {
        method: 'POST',
      });
      setMessage(result.number ? `${success} (${result.number})` : success);
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <CheckField label="Nur offene Bestellungen" checked={openOnly} onChange={setOpenOnly} />
        <span className="spacer" />
        {proposals.length > 0 && (
          <button onClick={() => setShowProposals(!showProposals)}>
            Bestellvorschlag ({proposals.length})
          </button>
        )}
        {mayEdit && (
          <button className="primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Abbrechen' : 'Neue Bestellung'}
          </button>
        )}
      </div>

      {showProposals && (
        <Card title="Bestellvorschlag" padded={false}>
          <div className="body small muted" style={{ paddingBottom: 0 }}>
            Artikel unter Mindestbestand, abzüglich dessen, was bereits bestellt und noch nicht
            geliefert ist.
          </div>
          <table>
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Eigentümer</th>
                <th>Lieferant</th>
                <th className="num">Bestand</th>
                <th className="num">Minimum</th>
                <th className="num">Bestellt</th>
                <th className="num">Vorschlag</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {proposals.map((suggestion) => (
                <tr key={suggestion.article_id}>
                  <td>
                    <span className="mono">{suggestion.sku}</span> {suggestion.article_name}
                  </td>
                  <td className="small">{suggestion.owner_name}</td>
                  <td className="small">
                    {suggestion.supplier_name ?? <span className="muted">nicht hinterlegt</span>}
                    {suggestion.lead_days !== null && suggestion.lead_days !== undefined && (
                      <div className="small muted">{suggestion.lead_days} Tage Lieferzeit</div>
                    )}
                  </td>
                  <td className="num" style={{ color: 'var(--bad)' }}>
                    {formatQty(suggestion.stock)}
                  </td>
                  <td className="num muted">{formatQty(suggestion.min_stock)}</td>
                  <td className="num muted">{formatQty(suggestion.on_order)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>
                    {formatQty(suggestion.shortfall, suggestion.unit)}
                  </td>
                  <td className="num">
                    {mayEdit && (
                      <button className="small ghost" onClick={() => addFromSuggestion(suggestion)}>
                        übernehmen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {showForm && (
        <Card title="Neue Bestellung">
          <div className="form-grid">
            <label className="field">
              <span>Eigentümer</span>
              <select value={draft.ownerId} onChange={(e) => setDraft({ ...draft, ownerId: e.target.value })}>
                <option value="">— bitte wählen —</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.is_self ? `${owner.name} · eigene Ware` : owner.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Lieferant</span>
              <select
                value={draft.supplierId}
                onChange={(e) => setDraft({ ...draft, supplierId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Lager</span>
              <select
                value={draft.warehouseId}
                onChange={(e) => setDraft({ ...draft, warehouseId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Referenz"
              value={draft.reference}
              onChange={(v) => setDraft({ ...draft, reference: v })}
            />
            <TextField
              label="Liefertermin"
              type="date"
              value={draft.expectedAt}
              onChange={(v) => setDraft({ ...draft, expectedAt: v })}
            />
          </div>

          {draft.ownerId && (
            <>
              <label className="field">
                <span>Artikel suchen</span>
                <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Artikelnummer oder Name" />
              </label>
              {hits.length > 0 && (
                <div className="pick-list">
                  {hits.map((hit) => (
                    <button
                      key={hit.id}
                      onClick={() => {
                        setDraftLines((c) => [
                          ...c,
                          { article: hit, qtyOrdered: '', price: fromCents(hit.purchase_price_cents) },
                        ]);
                        setTerm('');
                        setHits([]);
                      }}
                    >
                      <strong>{hit.name}</strong>
                      <div className="small muted mono">{hit.sku}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {draftLines.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Artikel</th>
                  <th className="num">Menge</th>
                  <th className="num">EK (€)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draftLines.map((line, index) => (
                  <tr key={`${line.article.id}-${index}`}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="mono">{line.article.sku}</span> {line.article.name}
                    </td>
                    <td className="num" style={{ width: 120 }}>
                      <input
                        style={{ textAlign: 'right' }}
                        value={line.qtyOrdered}
                        onChange={(e) =>
                          setDraftLines((c) =>
                            c.map((entry, i) => (i === index ? { ...entry, qtyOrdered: e.target.value } : entry)),
                          )
                        }
                      />
                    </td>
                    <td className="num" style={{ width: 110 }}>
                      <input
                        style={{ textAlign: 'right' }}
                        value={line.price}
                        onChange={(e) =>
                          setDraftLines((c) =>
                            c.map((entry, i) => (i === index ? { ...entry, price: e.target.value } : entry)),
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      <button className="small ghost" onClick={() => setDraftLines((c) => c.filter((_, i) => i !== index))}>
                        entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={createOrder} disabled={busy}>
              {busy ? 'Anlegen …' : 'Bestellung anlegen'}
            </button>
            <button onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <Card title="Bestellungen" padded={false}>
        {!orders ? (
          <Loading />
        ) : orders.length === 0 ? (
          <Empty>
            {openOnly ? (
              <>
                Keine offenen Bestellungen.{' '}
                <button className="ghost small" onClick={() => setOpenOnly(false)}>
                  Auch abgeschlossene zeigen
                </button>
              </>
            ) : (
              'Keine Bestellungen vorhanden.'
            )}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nummer</th>
                <th>Lieferant</th>
                <th>Lager</th>
                <th>Termin</th>
                <th>Status</th>
                <th className="num">Geliefert</th>
                <th className="num">Wert</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="mono">{order.number}</td>
                  <td className="small">{order.supplier_name}</td>
                  <td className="mono small">{order.warehouse_code}</td>
                  <td className="small">{formatDate(order.expected_at)}</td>
                  <td>
                    <Badge kind={STATUS_STYLE[order.status]}>{order.status}</Badge>
                  </td>
                  <td className="num small">
                    {formatQty(order.qty_delivered)} / {formatQty(order.qty_ordered)}
                  </td>
                  <td className="num">{formatMoney(order.value_cents)}</td>
                  <td className="num">
                    <button className="small ghost" onClick={() => setOpenId(order.id)}>
                      öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {openId && (
        <Card
          title={detail ? `Bestellung ${detail.order.number}` : 'Bestellung'}
          actions={
            <>
              {detail && mayEdit && detail.order.status === 'entwurf' && (
                <button className="primary small" onClick={() => act('place', 'Bestellung ausgelöst.')} disabled={busy}>
                  Bestellung auslösen
                </button>
              )}
              {detail && mayEdit && ['bestellt', 'teilgeliefert'].includes(detail.order.status) && (
                <button className="small" onClick={() => act('inbound', 'Avis erzeugt.')} disabled={busy}>
                  Avis erzeugen
                </button>
              )}
              {detail && mayEdit && detail.order.qty_delivered === 0 && detail.order.status !== 'storniert' && (
                <button className="small" onClick={() => act('cancel', 'Bestellung storniert.')} disabled={busy}>
                  stornieren
                </button>
              )}
              <button className="small" onClick={() => setOpenId(null)}>
                schliessen
              </button>
            </>
          }
          padded={false}
        >
          {!detail ? (
            <Loading />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Artikel</th>
                  <th className="num">Bestellt</th>
                  <th className="num">Geliefert</th>
                  <th className="num">Offen</th>
                  <th className="num">EK</th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.position}</td>
                    <td>
                      <span className="mono">{line.sku}</span> {line.article_name}
                    </td>
                    <td className="num">{formatQty(line.qty_ordered, line.unit)}</td>
                    <td className="num">{formatQty(line.qty_delivered)}</td>
                    <td className="num">
                      {line.qty_ordered - line.qty_delivered === 0
                        ? '—'
                        : formatQty(line.qty_ordered - line.qty_delivered)}
                    </td>
                    <td className="num">{formatMoney(line.price_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>
  );
}
